import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { EventType } from '../../../models/event-type';
import { Initiative } from '../../../models/initiative';
import { Language } from '../../../models/language';
import { InitiativeService } from '../../../services/initiative.service';
import { LanguageService } from '../../../services/language.service';
import { FlatRefData, refDataCollections, ReferenceDataService } from '../../../services/reference-data.service';
import { getGridTemplate } from '../../../utils/utils';
import { MatChipEditedEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TopicOfInterest } from '../../../models/topic-of-interest';


@Component({
    selector: 'app-flat-ref-data',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatChipsModule,
        DatePipe,
        MatSnackBarModule,
        MatTooltipModule
    ],
    templateUrl: './flat-ref-data.component.html',
    styleUrl: './flat-ref-data.component.scss',
})
export class FlatRefDataComponent implements OnInit, AfterViewInit {
    refDataCollections = refDataCollections;
    formBuilder = inject(NonNullableFormBuilder);
    refDataService = inject(ReferenceDataService);
    initiativeService = inject(InitiativeService);
    languageService = inject(LanguageService);
    snackBar = inject(MatSnackBar);
    separatorKeysCodes = [ENTER, COMMA] as const;
    readonly addOnBlur = true;

    allColumns = [
        { key: 'id', label: 'id', type: 'text' },
        { key: 'name', label: 'Name', type: 'longtext' },
        { key: 'lastModifiedOn', label: 'Last modified on', type: 'text' },
        { key: 'lastModifiedBy', label: 'Last modified by', type: 'text' },
    ];
    columns = ['name', 'lastModifiedOn', 'lastModifiedBy'];
    readonly loading = signal(false);
    private readonly route = inject(ActivatedRoute);
    dataSource = new MatTableDataSource<FlatRefData>();
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    readonly collection = this.route.snapshot.data['collection'] as string;
    data = signal<FlatRefData[]>([]);
    // id: number | null = null;
    // name = this.formBuilder.control<string>('', { validators: [Validators.required] });
    // mode = 'create';

    constructor() {
        effect(() => {
            console.debug('effect');
            const newItems = this.data();
            // Force a new array reference so MatTable registers the change
            this.dataSource.data = [...newItems];
        })
    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    loadData() {
        switch (this.collection) {
            case 'eventTypes':
                this.refDataService.refresh('eventTypes').subscribe({
                    next: v => {
                        this.data.set(v);
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            case 'languages':
                this.languageService.getAllLanguages().subscribe({
                    next: v => {
                        // v.sort((a, b) => a.name.localeCompare(b.name))
                        this.data.set(v);
                        // this.dataSource.data = this.data();
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                })
                break;
            case 'initiatives':
                this.initiativeService.getAllInitiatives().subscribe({
                    next: v => {
                        // v.sort((a, b) => a.name.localeCompare(b.name))
                        this.data.set(v);
                        // this.dataSource.data = this.data();
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                })
                break;
            case 'generalTopicsOfInterest':
                this.refDataService.refresh('generalTopicsOfInterest').subscribe({
                    next: v => {
                        this.data.set(v);
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            case 'memberTopicsOfInterest':
                this.refDataService.refresh('memberTopicsOfInterest').subscribe({
                    next: v => {
                        this.data.set(v);
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            default:
                console.error('unknown collection', this.collection);
                this.data.set([]);
        }
    }

    /**
     * Save a new item
     * @param event
     */

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        const inputElement = event.chipInput?.inputElement;
        const isBlurred = document.activeElement !== inputElement;
        if (!value) {
            if (!isBlurred)
                this.snackBar.open('❌ Name can\'t be empty', 'Close', { duration: 3000 });
            // Clear the input value
            event.chipInput!.clear();
            return;
        }

        switch (this.collection) {
            case 'eventTypes':
                this.refDataService.createFlatRefData(this.collection, { id: null, name: value }).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        event.chipInput!.clear();
                        this.refDataService.refresh(this.collection).subscribe(v => this.loadData());
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            case 'languages':
                this.languageService.create({ id: null, name: value }).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        // if (value) {
                        //   this.data.update(data => [...data, v]);
                        // }
                        event.chipInput!.clear();
                        this.loadData();
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            case 'initiatives':
                this.initiativeService.create({ id: null, name: value }).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        // if (value) {
                        //   this.data.update(data => [...data, v]);
                        // }
                        event.chipInput!.clear();
                        this.loadData();
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            case 'generalTopicsOfInterest':
            case 'memberTopicsOfInterest':
                this.refDataService.createFlatRefData(this.collection, { id: null, name: value }).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        event.chipInput!.clear();
                        this.refDataService.refresh(this.collection).subscribe(v => this.loadData());
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            default:
                console.error('unknown collection', this.collection);
                this.snackBar.open('❌ An error occured', 'Close', { duration: 3000 });
                return;
        }
    }

    edit(item: FlatRefData, event: MatChipEditedEvent) {
        const value = event.value.trim();
        if (!value) {
            this.snackBar.open('❌ Name can\'t be empty', 'Close', { duration: 3000 });
            return;
        }

        switch (this.collection) {
            case 'eventTypes':
                this.refDataService.updateFlatRefData(this.collection, { id: item.id!, name: value }).subscribe({
                    next: v => {
                        console.debug('updating item', v);
                        this.refDataService.refresh(this.collection).subscribe(v => this.loadData());
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            case 'languages':
                this.languageService.update({ id: item.id!, name: value }).subscribe({
                    next: v => {
                        console.debug('updating item', v);
                        this.loadData();
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            case 'initiatives':
                this.initiativeService.update({ id: item.id!, name: value }).subscribe({
                    next: v => {
                        console.debug('updating item', v);
                        this.loadData();
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            case 'generalTopicsOfInterest':
            case 'memberTopicsOfInterest':
                this.refDataService.updateFlatRefData(this.collection, { id: item.id!, name: value }).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        this.refDataService.refresh(this.collection).subscribe(v => this.loadData());
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            default:
                console.error('unknown collection', this.collection);
                this.snackBar.open('❌ An error occured', 'Close', { duration: 3000 });
                return;
        }
    }

    remove(item: FlatRefData) {
        this.snackBar.open('🤡 It is currently not possible to remove an item', 'Close', { duration: 3000 });
    }

    get gridTemplate() {
        return getGridTemplate(this.columns, this.allColumns);
    }
}
